import { z } from 'zod';
import { TEMPLATE_VARIABLE_ATTR } from '@/components/richText/templateVariableAttrs.ts';
import { getVariableLabel } from '@/components/richText/templateVariableDisplayNames.ts';
import { getMissingRequiredKeys } from '@/features/dashboard/messageTemplates/types/templateVariables.utils.ts';

const isBodyNotEmpty = (html: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const hasVariables =
    doc.querySelectorAll(`[${TEMPLATE_VARIABLE_ATTR}]`).length > 0;
  const hasText = (doc.body.textContent?.trim().length ?? 0) > 0;
  return hasVariables || hasText;
};

/**
 * Adds a superRefine that mirrors the backend `TEMPLATE_MISSING_REQUIRED_VARIABLE`
 * rule: every required variable must be output as `{{ key }}` in the body. Only
 * the body is validated — the subject is intentionally excluded.
 */
const requiredVariablesRefinement =
  (requiredKeys: string[]) =>
  (body: string, ctx: z.RefinementCtx): void => {
    const missing = getMissingRequiredKeys(body, requiredKeys);
    if (missing.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Brakuje wymaganych zmiennych w treści: ${missing
          .map(getVariableLabel)
          .join(', ')}`,
      });
    }
  };

export const createEditEmailTemplateSchema = (requiredKeys: string[]) =>
  z.object({
    subjectTemplate: z.string().min(1, 'Temat jest wymagany'),
    bodyTemplate: z
      .string()
      .refine(isBodyNotEmpty, 'Treść jest wymagana')
      .superRefine(requiredVariablesRefinement(requiredKeys)),
  });

export const createEditSmsTemplateSchema = (requiredKeys: string[]) =>
  z.object({
    bodyTemplate: z
      .string()
      .min(1, 'Treść jest wymagana')
      .superRefine(requiredVariablesRefinement(requiredKeys)),
  });

export type EditEmailTemplateFormData = z.infer<
  ReturnType<typeof createEditEmailTemplateSchema>
>;
export type EditSmsTemplateFormData = z.infer<
  ReturnType<typeof createEditSmsTemplateSchema>
>;

export type EditTemplateFormData =
  | EditEmailTemplateFormData
  | EditSmsTemplateFormData;
